import React, { useEffect } from 'react';

import { useUser } from 'contexts/UserContext';

import numeral from 'utils/numeral';
import { ACTIVATION } from 'utils/rest';

import { Container, Item, Row } from 'components/Layout';
import Loading from 'components/Loading';
import { TableData } from 'components/TableData';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { useNavigation } from '../../../contexts/NavigationContext';

import { ModalForm } from './components';

const buildOfflineRowsForChannel = (offlineCpmList, channelKey, channelDbId) => {
  if (!Array.isArray(offlineCpmList)) {
    return [];
  }
  return offlineCpmList
    .filter(item => {
      const ch = item.channel;
      if (ch && typeof ch === 'object') {
        if (channelKey && ch.key === channelKey) {
          return true;
        }
        if (channelDbId && String(ch._id || ch.id) === String(channelDbId)) {
          return true;
        }
        return false;
      }
      if (channelDbId) {
        return String(ch) === String(channelDbId);
      }
      return false;
    })
    .map(item => ({
      _id: item._id,
      offlineMediaProxy: item.offlineMediaProxy?.name ?? item.offlineMediaProxy,
      cpm: item.cpm
    }));
};

const ConstraintChannelsContainer = () => {
  const { currentPresetId, canEdit } = useNavigation();
  const { adminRights } = useUser();
  const [rows, setRows] = React.useState([]);
  const [constraintChannel, setConstraintChannelList] = React.useState([]);
  const [fees, setfeesList] = React.useState([]);
  const [offlineMediaProxies, setOfflineMediaProxies] = React.useState([]);
  const [forcedChannelsList, setForcedChannelsList] = React.useState([]);
  const [offlineCpmList, setOfflineCpmList] = React.useState([]);
  const [activationChannels, setActivationChannels] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [openModal, setModal] = React.useState(null);
  const [
    selectedConstraintChannel,
    setSelectedConstraintChannel
  ] = React.useState({});

  useEffect(() => {
    setLoaded(false);
  }, [currentPresetId]);

  const selectConstraintChannel = id => {
    const selected = constraintChannel.find(el => el._id === id);
    setSelectedConstraintChannel(selected);
  };
  const closeModal = () => {
    setModal(null);
    setSelectedConstraintChannel({});
  };
  const getConstraintsChannels = async () => {
    try {
      const result = await ACTIVATION.getConstraintsChannels(currentPresetId);
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getFees = async () => {
    try {
      const result = await ACTIVATION.getFees(currentPresetId);
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getOfflineMediaProxies = async () => {
    try {
      const result = await ACTIVATION.getOfflineMediaProxies();
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getForcedChannels = async () => {
    try {
      const result = await ACTIVATION.getForcedChannel(currentPresetId);
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getOfflineCpm = async () => {
    try {
      const result = await ACTIVATION.getOfflineCPM(currentPresetId);
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getChannels = async () => {
    try {
      const result = await ACTIVATION.getChannels();
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const openUpdateModal = id => {
    if (adminRights?.activation?.update) {
      selectConstraintChannel(id);
      setModal('ModalUpdate');
    } else {
      setModal('unauthorized');
    }
  };

  const getProxyIdByName = (name, proxies) => {
    const found = proxies.find(p => p.name === name);
    if (!found) {
      return null;
    }
    return found.id || found._id;
  };

  const syncForcedChannel = async (channelKey, shareStr, initialForced) => {
    const trimmed = typeof shareStr === 'string' ? shareStr.trim() : '';
    if (!trimmed) {
      if (initialForced && initialForced._id) {
        await ACTIVATION.deleteForcedChannel(initialForced._id);
      }
      return;
    }
    const num = Number(trimmed);
    const percent = num / 100;
    if (initialForced && initialForced._id) {
      await ACTIVATION.updateForcedChannel(initialForced._id, {
        percent,
        channel: channelKey
      });
      return;
    }
    await ACTIVATION.createForcedChannel({
      preset: currentPresetId,
      percent,
      channel: channelKey
    });
  };

  const syncOfflineCpmDiff = async ({
    channelDbId,
    initialRows,
    currentRows,
    proxies
  }) => {
    const initialSnap = Array.isArray(initialRows) ? initialRows.map(r => ({ ...r })) : [];
    const currentSnap = Array.isArray(currentRows) ? currentRows.map(r => ({ ...r })) : [];
    const currentIds = new Set(currentSnap.filter(r => r._id).map(r => String(r._id)));
    const initialById = new Map(initialSnap.filter(r => r._id).map(r => [String(r._id), r]));

    for (const row of initialSnap) {
      if (row._id && !currentIds.has(String(row._id))) {
        await ACTIVATION.deleteOfflineCpm(row._id);
      }
    }

    for (const row of currentSnap) {
      const proxyId = getProxyIdByName(row.offlineMediaProxy, proxies);
      if (!row._id) {
        if (!proxyId) {
          continue;
        }
        await ACTIVATION.createOfflineCPM({
          preset: currentPresetId,
          channel: channelDbId,
          offlineMediaProxy: proxyId,
          cpm: Number(row.cpm),
          user: null
        });
        continue;
      }

      const prev = initialById.get(String(row._id));
      if (!prev) {
        continue;
      }
      const prevName =
        typeof prev.offlineMediaProxy === 'object' && prev.offlineMediaProxy
          ? prev.offlineMediaProxy.name
          : prev.offlineMediaProxy;
      const sameAudience = String(prevName || '') === String(row.offlineMediaProxy || '');
      const sameCpm = Number(prev.cpm) === Number(row.cpm);
      if (!sameAudience || !sameCpm) {
        await ACTIVATION.updateOfflineCPM(row._id, {
          cpm: Number(row.cpm),
          offlineMediaProxy: proxyId,
          channel: channelDbId,
          user: null
        });
      }
    }
  };

  const onUpdate = async payload => {
    const {
      constraintChannel: constraintDatas,
      mandatoryBudgetShare,
      offlineCpmRows,
      initialForcedChannel,
      initialOfflineCpmRows,
      channelKey,
      channelDbId
    } = payload;

    await ACTIVATION.updateConstraintsChannels(selectedConstraintChannel._id, constraintDatas);
    await syncForcedChannel(channelKey, mandatoryBudgetShare, initialForcedChannel);
    await syncOfflineCpmDiff({
      channelDbId,
      initialRows: initialOfflineCpmRows,
      currentRows: offlineCpmRows,
      proxies: offlineMediaProxies
    });
    setLoaded(false);
    closeModal();
  };

  if (!loaded) {
    Promise.all([
      getConstraintsChannels(),
      getFees(),
      getOfflineMediaProxies(),
      getForcedChannels(),
      getOfflineCpm(),
      getChannels()
    ]).then(resp => {
      const [
        constraints,
        fees,
        offlineMediaProxiesData,
        forcedChannels,
        offlineCpmData,
        channelsData
      ] = resp;

      setLoaded(true);
      setConstraintChannelList(constraints);
      setfeesList(fees);
      setOfflineMediaProxies(offlineMediaProxiesData);
      setForcedChannelsList(Array.isArray(forcedChannels) ? forcedChannels : []);
      setOfflineCpmList(Array.isArray(offlineCpmData) ? offlineCpmData : []);
      setActivationChannels(Array.isArray(channelsData) ? channelsData : []);
 
      const mapped = constraints.map(item => {
        const newItem = {};
        newItem._id = item._id;
        newItem.channel = item.channel;
        newItem.start = item.start;
        newItem.duration = item.duration;
        newItem.minBudget = item.minBudget;
        newItem.format = item.format !== 'none' ? item.format : 'aucun';
        newItem.maxRepeat = item.maxRepeat;
        newItem.averageCPM = item.averageCPM;
        newItem.offTargetISC = numeral(item.offTargetISC).format('0[.]00%');
        newItem.fee = resp[1].find(el => el._id === item.fee)?.key || 'Aucun';
        newItem.memoBeta = item.memoBeta;
        return newItem;
      });
      setRows(mapped);
    });
  }

  const channelKey = selectedConstraintChannel.channel;
  const channelEntity = activationChannels.find(c => c.key === channelKey);
  const channelDbId = channelEntity ? channelEntity.id || channelEntity._id : null;
  const initialForcedChannel =
    forcedChannelsList.find(fc => fc.channel === channelKey) || null;
  const initialOfflineCpmRows = buildOfflineRowsForChannel(
    offlineCpmList,
    channelKey,
    channelDbId
  );

  return (
    <Container>
      <Row spacing={4}>
        <Item>
          <Loading loading={!loaded} />
          {loaded && (
            <TableData
              rows={rows}
              headers={[
                {
                  label: 'Canal',
                  id: 'channel',
                  sortKey: 'channel'
                },
                {
                  label: 'Délai(jours)',
                  id: 'start'
                },
                {
                  label: 'Durée minimale(jours)',
                  id: 'duration'
                },
                {
                  label: 'Budget minimum(€)',
                  id: 'minBudget'
                },
                {
                  label: 'Format nécessaire',
                  id: 'format'
                },
                {
                  label: 'Répétition maximale',
                  id: 'maxRepeat'
                },
                {
                  label: 'CPM moyen(€)',
                  id: 'averageCPM'
                },
                {
                  label: 'Taux d’impressions sur cible hors data moyen',
                  id: 'offTargetISC'
                },
                {
                  label: 'Groupe tarifaire',
                  id: 'fee'
                },
                {
                  label: 'Beta de mémorisation',
                  id: 'memoBeta'
                }
              ]}
              hidenFields={['_id']}
              onUpdate={canEdit && openUpdateModal}
              onView={!canEdit && openUpdateModal}
            />
          )}

          {openModal === 'ModalUpdate' && (
            <ModalForm
              key={selectedConstraintChannel._id}
              open={openModal === 'ModalUpdate'}
              onClose={closeModal}
              onValidate={onUpdate}
              config={{
                ...selectedConstraintChannel,
                fees,
                offlineMediaProxies,
                initialForcedChannel,
                initialOfflineCpmRows,
                channelKey,
                channelDbId
              }}
              actionName="Modifier"
              canEdit={canEdit}
            />
          )}
          {openModal === 'unauthorized' && (
            <UnauthorizedModal
              open={openModal === 'unauthorized'}
              onClose={closeModal}
            />
          )}
        </Item>
      </Row>
    </Container>
  );
};

export default ConstraintChannelsContainer;
